/**
 * SIMon AI Shopping Agent — Client SDK
 * OpenGolfer.co.uk
 */
(function() {
  'use strict';

  var LOG = '[OG-SIMon]';
  var API_URL = 'https://simon-api.opengolfer.workers.dev';
  var SESSION_KEY = 'og_simon_session';
  var DISMISS_KEY = 'og_simon_cta_dismissed';

  // State
  var isOpen = false;
  var isStreaming = false;
  var messages = [];
  var sessionId = '';
  var pageContext = window.__SIMON_CTX || { page_type: 'index', url: '/' };

  // DOM refs (set in init)
  var trigger, modal, messagesEl, form, input, chips, closeBtn, cta, ctaClose, backdrop;

  // ─── Init ───
  function init() {
    console.log(LOG, 'Initializing...');

    // Generate session ID
    sessionId = getSessionId();

    // Get DOM refs
    trigger = document.getElementById('og-simon-trigger');
    modal = document.getElementById('og-simon-modal');
    messagesEl = document.getElementById('og-simon-messages');
    form = document.getElementById('og-simon-form');
    input = document.getElementById('og-simon-input');
    chips = document.getElementById('og-simon-chips');
    closeBtn = document.getElementById('og-simon-close');
    cta = document.getElementById('og-simon-cta');

    if (!trigger || !modal) {
      console.warn(LOG, 'DOM elements not found');
      return;
    }

    backdrop = modal.querySelector('.og-si-modal-backdrop');
    ctaClose = trigger.querySelector('.og-si-cta-close');

    // Event listeners
    trigger.addEventListener('click', function(e) {
      if (e.target === ctaClose || (ctaClose && ctaClose.contains(e.target))) return;
      openModal();
    });

    trigger.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openModal();
      }
    });

    closeBtn.addEventListener('click', closeModal);

    backdrop.addEventListener('click', function(e) {
      if (e.target === backdrop) closeModal();
    });

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      var text = input.value.trim();
      if (text && !isStreaming) {
        sendMessage(text);
        input.value = '';
      }
    });

    // Suggestion chips
    var chipButtons = chips.querySelectorAll('.og-si-chip');
    for (var i = 0; i < chipButtons.length; i++) {
      chipButtons[i].addEventListener('click', function() {
        var msg = this.getAttribute('data-msg');
        if (msg && !isStreaming) {
          sendMessage(msg);
        }
      });
    }

    // CTA dismiss
    if (ctaClose) {
      ctaClose.addEventListener('click', function(e) {
        e.stopPropagation();
        cta.style.display = 'none';
        try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch(err) { /* noop */ }
      });
    }

    // ESC to close
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) closeModal();
    });

    // Eye tracking
    document.addEventListener('mousemove', trackEyes);

    // Show trigger after delay
    var delay = pageContext.page_type === 'product' ? 3000 : 5000;
    setTimeout(function() {
      trigger.style.display = 'flex';
      showCta();
    }, delay);

    console.log(LOG, 'Ready. Page:', pageContext.page_type);
  }

  // ─── Session ID ───
  function getSessionId() {
    var id = '';
    try {
      id = ''; // fresh id per page load: the chat history does not survive a reload
    } catch(e) { /* noop */ }

    if (!id) {
      id = 'si_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
      try { sessionStorage.setItem(SESSION_KEY, id); } catch(e) { /* noop */ }
    }
    return id;
  }

  // ─── Show CTA ───
  function showCta() {
    if (window.innerWidth <= 768) return; // No CTA on mobile

    var dismissed = false;
    try { dismissed = sessionStorage.getItem(DISMISS_KEY) === '1'; } catch(e) { /* noop */ }
    if (dismissed) return;

    var ctaDelay = pageContext.page_type === 'product' ? 8000 : 12000;
    setTimeout(function() {
      if (!isOpen && cta) {
        cta.style.display = 'flex';
      }
    }, ctaDelay);
  }

  // ─── Open / Close Modal ───
  function openModal() {
    if (isOpen) return;
    isOpen = true;
    console.log(LOG, 'Opening modal');

    modal.style.display = 'flex';
    trigger.style.display = 'none';
    if (cta) cta.style.display = 'none';
    document.body.classList.add('og-si-no-scroll');

    // Focus input
    setTimeout(function() { input.focus(); }, 100);

    // Send greeting if first open
    if (messages.length === 0) {
      addGreeting();
    }
  }

  function closeModal() {
    if (!isOpen) return;
    isOpen = false;
    console.log(LOG, 'Closing modal');

    modal.style.display = 'none';
    trigger.style.display = 'flex';
    document.body.classList.remove('og-si-no-scroll');

    // Log conversation if we had any user messages
    var userMsgs = messages.filter(function(m) { return m.role === 'user'; });
    if (userMsgs.length > 0) {
      logConversation();
    }
  }

  // ─── Greeting ───
  function addGreeting() {
    var greeting = '';
    switch (pageContext.page_type) {
      case 'product':
        greeting = 'Hey! I see you\'re checking out the **' + (pageContext.product_title || 'this product') + '**. Want help figuring out the right size for your room, or comparing it with other options?';
        break;
      case 'collection':
        greeting = 'Lots of options here! Tell me your room size and budget, and I\'ll narrow it down to 2-3 perfect choices for you.';
        break;
      default:
        greeting = 'Hey! I\'m SIMon, your golf simulator expert. Looking to bring the driving range home? Tell me about your space and budget, and I\'ll find the perfect setup.';
    }

    messages.push({ role: 'assistant', content: greeting });
    appendMessage('assistant', greeting);
  }

  // ─── Send Message ───
  function sendMessage(text) {
    if (isStreaming) return;

    // Hide chips after first message
    if (chips) chips.style.display = 'none';

    // Add user message
    messages.push({ role: 'user', content: text });
    appendMessage('user', text);

    // Show typing indicator
    showTyping();

    // Stream response
    streamResponse();
  }

  // ─── Stream Response from API ───
  function streamResponse() {
    isStreaming = true;
    var fullText = '';
    var streamBubble = null;

    // Build messages for API (exclude system greeting if needed)
    var apiMessages = messages.map(function(m) {
      return { role: m.role, content: m.content };
    });

    fetch(API_URL + '/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: apiMessages,
        session_id: sessionId,
        page_context: pageContext,
        attribution: window.__ogAttr ? window.__ogAttr() : null
      })
    }).then(function(response) {
      if (!response.ok) {
        throw new Error('API error: ' + response.status);
      }

      var reader = response.body.getReader();
      var decoder = new TextDecoder();
      var buffer = '';

      hideTyping();

      function processChunk() {
        return reader.read().then(function(result) {
          if (result.done) {
            // Finalize
            if (fullText) {
              messages.push({ role: 'assistant', content: fullText });
            }
            isStreaming = false;
            scrollToBottom();
            return;
          }

          buffer += decoder.decode(result.value, { stream: true });
          var lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (var i = 0; i < lines.length; i++) {
            var line = lines[i].trim();
            if (!line) continue;

            if (line.startsWith('event: ')) {
              var eventType = line.substring(7);
              // Next line should be data
              i++;
              if (i < lines.length && lines[i].startsWith('data: ')) {
                var dataStr = lines[i].substring(6);
                try {
                  var data = JSON.parse(dataStr);
                  handleSSE(eventType, data);
                } catch(e) {
                  console.warn(LOG, 'Parse error:', e);
                }
              }
            } else if (line.startsWith('data: ')) {
              // Data without explicit event
              var dataStr2 = line.substring(6);
              try {
                var data2 = JSON.parse(dataStr2);
                handleSSE('text', data2);
              } catch(e) { /* noop */ }
            }
          }

          return processChunk();
        });
      }

      function handleSSE(eventType, data) {
        switch (eventType) {
          case 'text':
            if (data.delta) {
              fullText += data.delta;
              if (!streamBubble) {
                streamBubble = createStreamBubble();
              }
              updateStreamBubble(streamBubble, fullText);
              scrollToBottom();
            }
            break;
          case 'tool_use':
            showToolUse(data.tool);
            break;
          case 'error':
            hideTyping();
            appendMessage('assistant', 'Sorry, that reply got cut off. Ask again, or tap WhatsApp below and he\'ll pick it up.');
            isStreaming = false;
            break;
          case 'done':
            // Handled in processChunk done
            break;
        }
      }

      return processChunk();
    }).catch(function(err) {
      console.error(LOG, 'Stream error:', err);
      hideTyping();
      appendMessage('assistant', 'Hmm, I\'m having trouble connecting. Try again in a moment, or tap WhatsApp below.');
      isStreaming = false;
    });
  }

  // ─── DOM Helpers ───
  function appendMessage(role, content) {
    var msg = document.createElement('div');
    msg.className = 'og-si-msg og-si-msg--' + role;

    var avatar = document.createElement('div');
    avatar.className = 'og-si-msg-avatar';
    if (role === 'user') {
      avatar.textContent = 'Y';
    }

    var bubble = document.createElement('div');
    bubble.className = 'og-si-msg-bubble';
    bubble.innerHTML = renderMarkdown(content);

    msg.appendChild(avatar);
    msg.appendChild(bubble);
    messagesEl.appendChild(msg);
    scrollToBottom();
  }

  function createStreamBubble() {
    // Remove typing indicator if present
    hideTyping();

    var msg = document.createElement('div');
    msg.className = 'og-si-msg og-si-msg--assistant';
    msg.id = 'og-simon-streaming';

    var avatar = document.createElement('div');
    avatar.className = 'og-si-msg-avatar';

    var bubble = document.createElement('div');
    bubble.className = 'og-si-msg-bubble';

    msg.appendChild(avatar);
    msg.appendChild(bubble);
    messagesEl.appendChild(msg);
    return bubble;
  }

  function updateStreamBubble(bubble, text) {
    bubble.innerHTML = renderMarkdown(text);
  }

  function showTyping() {
    if (document.getElementById('og-simon-typing')) return;

    var wrapper = document.createElement('div');
    wrapper.className = 'og-si-msg og-si-msg--assistant';
    wrapper.id = 'og-simon-typing';

    var avatar = document.createElement('div');
    avatar.className = 'og-si-msg-avatar';

    var indicator = document.createElement('div');
    indicator.className = 'og-si-typing-indicator';
    indicator.innerHTML = '<div class="og-si-typing-dot"></div><div class="og-si-typing-dot"></div><div class="og-si-typing-dot"></div>';

    wrapper.appendChild(avatar);
    wrapper.appendChild(indicator);
    messagesEl.appendChild(wrapper);
    scrollToBottom();
  }

  function hideTyping() {
    var el = document.getElementById('og-simon-typing');
    if (el) el.remove();
  }

  function showToolUse(toolName) {
    var label = toolName === 'search_products' ? 'Searching products...' : 'Looking up details...';
    var el = document.createElement('div');
    el.className = 'og-si-tool-msg';
    el.textContent = label;
    messagesEl.appendChild(el);
    scrollToBottom();

    // Remove after a few seconds
    setTimeout(function() {
      if (el.parentNode) el.remove();
    }, 5000);
  }

  function scrollToBottom() {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  // ─── Markdown Renderer (minimal, XSS-safe) ───
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function sanitizeUrl(url) {
    var trimmed = url.trim();
    if (trimmed.indexOf('https://') === 0 || trimmed.indexOf('http://') === 0 || trimmed.indexOf('/') === 0) {
      return trimmed;
    }
    return '#';
  }

  function renderMarkdown(text) {
    if (!text) return '';
    // Escape HTML entities first to prevent XSS
    var escaped = escapeHtml(text);
    var html = escaped
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Links (sanitize URLs to block javascript: protocol)
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, function(match, linkText, url) {
        return '<a href="' + sanitizeUrl(url) + '" target="_blank" rel="noopener">' + linkText + '</a>';
      })
      // Unordered lists
      .replace(/^[\-\*] (.+)$/gm, '<li>$1</li>')
      // Paragraphs
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');

    // Wrap consecutive <li> in <ul>
    html = html.replace(/(<li>.*?<\/li>)+/g, '<ul>$&</ul>');
    // Wrap in <p> if not already
    if (html.indexOf('<ul>') !== 0 && html.indexOf('<p>') !== 0) {
      html = '<p>' + html + '</p>';
    }
    return html;
  }

  // ─── Eye Tracking ───
  function trackEyes(e) {
    if (isOpen) return; // Don't track when modal is open

    var ball = trigger ? trigger.querySelector('.og-si-ball') : null;
    if (!ball) return;

    var rect = ball.getBoundingClientRect();
    var centerX = rect.left + rect.width / 2;
    var centerY = rect.top + rect.height / 2;

    var dx = e.clientX - centerX;
    var dy = e.clientY - centerY;
    var dist = Math.sqrt(dx * dx + dy * dy);

    // Max 2px travel
    var maxTravel = 2;
    var moveX = (dx / Math.max(dist, 1)) * Math.min(dist / 100, maxTravel);
    var moveY = (dy / Math.max(dist, 1)) * Math.min(dist / 100, maxTravel);

    var pupils = ball.querySelectorAll('.og-si-pupil');
    for (var i = 0; i < pupils.length; i++) {
      pupils[i].style.transform = 'translate(' + moveX + 'px, ' + moveY + 'px)';
    }
  }

  // ─── Log Conversation ───
  function logConversation() {
    try {
      fetch(API_URL + '/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          page_url: pageContext.url || window.location.pathname,
          page_type: pageContext.page_type,
          messages: messages
        })
      }).catch(function(err) {
        console.warn(LOG, 'Log error:', err);
      });
    } catch(e) {
      /* noop */
    }
  }

  // ─── Public API (for banner CTA) ───
  window.OGSimon = {
    open: function() { openModal(); }
  };

  // ─── Boot ───
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
